import { AfterViewInit, Component, Inject, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatTreeComponent, TermsConditionsDialogComponent } from '@serviceplan/ui-kit';
import { AssetNestedTreeNode, DownloadAssetsService } from '@thehubz/client/asset-services';
import {
  CanPipe,
  ClientEnvironment,
  ClientNotificationHandlerService,
  CommonUtilityLib,
  DateNotExpiredPipe,
  ENVIRONMENT,
  PROMPTS,
  anyAsset,
} from '@thehubz/client/core';
import { Asset, AssetDetails, AssetsApiService, BuyoutRights, Collection, CollectionsApiService } from '@thehubz/client/data-access-api';
import { CustomClients } from '@thehubz/shared/branding';
import { AnyType, LicenseRegion, createAssetS3Key } from '@thehubz/shared/model';
import { firstValueFrom, timer } from 'rxjs';
import { DownloadPurposeData, SelectFileOptions } from '../../models/download-purpose-data';
import { AssetCommonService } from '../../services/asset-common.service';

export interface AssetDownloadData {
  data: { downloadData: AssetNestedTreeNode[]; assets: Asset[] } | Promise<{ downloadData: AssetNestedTreeNode[]; assets: Asset[] }>;
  downloadType?: string;
  collectionId?: string;
  assetInfo?: AnyType;
  module?: string;
}

@Component({
  selector: 'thehubz-asset-download',
  templateUrl: './asset-download.component.html',
  styleUrls: ['./asset-download.component.scss'],
})
export class AssetDownloadComponent implements AfterViewInit {
  PROMPTS = PROMPTS;
  downloadData?: AssetNestedTreeNode[];
  assets?: Asset[];
  allPurposes = [DownloadPurposeData];
  allSelectFileOptions = SelectFileOptions;
  licenseRegion = LicenseRegion;
  downloadDetails: AnyType[] = [];
  assetAspectRatio: AssetDetails[] = [];
  selectedLicenseRegionValues: string[] = [];
  matTreeCheckbox = false;
  isMatTreeSelected = false;
  checked = false;
  downloadType?: string;
  collectionId?: string;
  errorMsg = '';
  showExpiryDate = true;
  mandatoryFieldsFlag = true;

  readonly brandingKey!: CustomClients;
  readonly CustomClients = CustomClients;
  readonly AnyAsset = anyAsset;

  downloadForm: UntypedFormGroup;

  @ViewChild(MatTreeComponent) matTree!: MatTreeComponent;

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: AssetDownloadData,
    @Inject(ENVIRONMENT) protected readonly env: ClientEnvironment,
    private canPipe: CanPipe,
    private matDialog: MatDialog,
    private assetService: AssetsApiService,
    private _formBuilder: UntypedFormBuilder,
    private commonUtilityLib: CommonUtilityLib,
    private dateNotExpiredPipe: DateNotExpiredPipe,
    protected assetCommonService: AssetCommonService,
    private downloadAssetsService: DownloadAssetsService,
    protected collectionAPIService: CollectionsApiService,
    private dialogRef: MatDialogRef<AssetDownloadComponent>,
    private clientNotificationHandlerService: ClientNotificationHandlerService,
  ) {
    this.brandingKey = env.brandingKey ?? CustomClients.Standard;

    if (data.data instanceof Promise) {
      data.data.then((res) => {
        this.downloadData = res.downloadData;
        this.assets = res.assets;
        this.initAssets();
      });
    } else {
      this.downloadData = data.data.downloadData;
      this.assets = data.data.assets;
      this.initAssets();
    }

    const enableTerms = this.env.termsConditionsPopup?.onAssetDownload ? false : true;
    this.downloadForm = this._formBuilder.group({
      isActive: [true],
      licenseRegion: [''],
      purpose: ['', this.brandingKey !== CustomClients.WestLotto && this.brandingKey !== CustomClients.Standard ? [Validators.required] : []],
      selectedOptions: [''],
      selected: ['', [Validators.required]],
      euAIAct: [false, this.brandingKey === CustomClients.SEAT ? [Validators.requiredTrue] : []],
      expiryAndTerms: [enableTerms, [Validators.requiredTrue]],
    });
    if (this.brandingKey === CustomClients.LufthansaMediacenter || this.brandingKey === CustomClients.LufthansaMedialounge) {
      this.showExpiryDate = false;
    }
    if ([CustomClients.SEAT, CustomClients.LufthansaMediacenter, CustomClients.LufthansaMedialounge, CustomClients.Arval].includes(this.brandingKey)) {
      this.mandatoryFieldsFlag = false;
      this.downloadForm.get('purpose')?.clearValidators();
      this.downloadForm.get('purpose')?.updateValueAndValidity();
    }
  }

  private initAssets() {
    this.downloadType = this.data.downloadType;
    this.collectionId = this.data.collectionId;
    timer(10).subscribe(() => {
      if (this.allSelectFileOptions?.length > 0) {
        this.downloadForm.get('selectedOptions')?.setValue([this.allSelectFileOptions[0]]);
      }
      this.checkOriginalFiles(this.downloadData ?? []);
    });
  }

  ngAfterViewInit(): void {
    // Subscribe to changes in the selected assets
    this.downloadForm.get('selected')?.valueChanges.subscribe((data) => {
      // Check if the user has access to assets
      const assetAccess = this.canPipe.transform(this.AnyAsset, 'expired.download');
      // Filter out expired assets if the user doesn't have access or if the asset's expiry date has not passed
      const filterExpiredAssets = data.filter((asset: AnyType) => this.dateNotExpiredPipe.transform(asset.expiryDate) || assetAccess);
      // Check if any of the filtered assets have the indeterminate property set to true
      const anySelected: boolean = filterExpiredAssets.some((asset: AnyType) => asset.indeterminate);
      // Check if any options are selected
      if (!anySelected) {
        // If no options are selected, set an error on the 'selected' form control
        this.downloadForm.get('selected')?.setErrors({ required: !anySelected });
      } else {
        // If options are selected, set null error on the 'selected' form control
        this.downloadForm.get('selected')?.setErrors(null);
      }
      // Clear the error message
      this.errorMsg = '';
      // If there are filtered assets and none are selected, display an error message
      if (filterExpiredAssets.length && !anySelected) {
        this.errorMsg = $localize`Please select any asset to download`;
      } // If no assets are selected and the user doesn't have access to expired assets, display an error message
      else if (!anySelected && !assetAccess) {
        this.errorMsg = $localize`You are not allowed to download expired asset`;
      }
    });
  }

  async onSubmit() {
    let channels = '';
    let licenseRegion = '';
    if (this.downloadForm.value['purpose']) {
      channels = this.downloadForm.value['purpose'].map((purpose: AnyType) => purpose.name).join(', ');
    }
    if (this.downloadForm.value['licenseRegion']) {
      licenseRegion = this.downloadForm.value['licenseRegion'].map((region: AnyType) => region.name).join(', ');
    }

    const assetDownloadHistory = await Promise.all(
      this.assets?.map(async (asset: Asset) => {
        const history = await this.assetCommonService.assetDownloadHistory(asset, this.data?.module ?? 'Library');
        history.channel = channels;
        history.licenseRegion = licenseRegion ? licenseRegion : undefined;
        return history;
      }) ?? [],
    );

    const keys = this.generateDownloadKeys();
    let name: string = document.title.replace(/ /g, '');

    if (this.env.assetDownload?.fileName) {
      name = this.env.assetDownload?.fileName;
    } else if (this.env.appTitle && this.env.brandingTitle) {
      name = `${this.env.brandingTitle.replace(/ /g, '_')}_${this.env.appTitle.replace(/ /g, '_')}`;
    }

    const assetCount = this.downloadData?.length ?? 0;

    if (keys.length > 1) {
      if (this.collectionId) {
        this.collectionAPIService.collectionsControllerFindOne(this.collectionId).subscribe(async (resp: Collection) => {
          const collectionName = resp.name;
          this.clientNotificationHandlerService.successNotification(
            $localize`Please wait. The  ${assetCount > 1 ? 'assets' : 'asset '} from "${collectionName}" ${assetCount > 1 ? ' are ' : 'is '}  being downloaded`,
          );

          const collectionReportName = this.env.assetDownload?.collectionInitial ? this.env.assetDownload?.collectionInitial + '_' : '';
          this.downloadAssetsService.downloadFiles(
            keys,
            `${collectionReportName}${collectionName}_${this.timestampForFilename()}.zip`,
            $localize`Selected collection`,
            assetDownloadHistory,
          );
        });
      } else {
        this.clientNotificationHandlerService.successNotification(
          $localize`Please wait. The  ${assetCount > 1 ? 'assets are ' : 'asset is '} being downloaded`,
        );
        this.downloadAssetsService.downloadFiles(keys, `${name}_${this.timestampForFilename()}.zip`, $localize`Selected assets`, assetDownloadHistory);
      }
    } else {
      const keyParts = keys[0].split('/');
      const node = this.downloadData?.find((node: AnyType) => node.assetId === keyParts[1]);
      if (node) {
        const filePart = keyParts[keyParts.length - 1];
        // download main file of asset or any other file
        if (filePart.startsWith('main.')) {
          this.assetService.assetsControllerFindOne(node.assetId).subscribe({
            next: async (assetObj) => {
              // this.data.assetInfo is send from sharing client
              const assetDownloadObject = this.commonUtilityLib.isEmptyObject(assetObj ?? {}) ? this.data.assetInfo : assetObj;
              // this.data.module is send from sharing client
              await this.assetCommonService.downloadAsset(assetDownloadObject, this.data.module ?? 'Library', { channels, licenseRegion }, this.checked);
            },
            error: (err) => {
              this.clientNotificationHandlerService.errorNotification(err);
            },
          });
        } else {
          const name = node.name + '_' + filePart;
          this.downloadAssetsService.downloadFiles(keys, name, $localize`Selected assets`, assetDownloadHistory);
        }
      }
    }
    this.dialogRef.close();
  }

  removeChips(toRemove: AnyType, formControlName: string, data?: AssetNestedTreeNode[]) {
    const formControlValue = this.downloadForm.get(formControlName)?.value;
    const newFormControlValue = this.commonUtilityLib.removeObjectFromArray(formControlValue, toRemove.list._id);
    if (formControlName === 'licenseRegion') {
      this.selectedLicenseRegionValues = newFormControlValue.length ? newFormControlValue.map((val: AnyType) => val._id) : [];
      this.updateLicenseRegionFormControl(this.selectedLicenseRegionValues);
    }
    if (formControlName === 'selectedOptions' && data) {
      const event = { value: newFormControlValue };
      this.onSelectedChange(event, data);
    }
    this.downloadForm.get(formControlName)?.setValue(newFormControlValue);
  }

  onSelectedChange(event: AnyType, data: AssetNestedTreeNode[]) {
    if (event.value.length > 0) {
      this.checkOriginalFiles(data, event.value);
    } else {
      this.matTree.setAll(false);
    }
  }

  checkOriginalFiles(data: AssetNestedTreeNode[], selectedOptions?: AnyType[]) {
    this.matTree.setAll(false);
    if (this.matTree.treeControl) {
      for (let i = 0; i < data.length; i++) {
        let item;
        if (selectedOptions && selectedOptions.length > 0) {
          selectedOptions.forEach((option: AnyType) => {
            item = this.matTree.treeControl.dataNodes?.[i]?.children?.[0]?.children?.find((child: AnyType) => child.fileNameRef === option._id);
            this.updateSelectedOptions(item, data, i);
          });
        } else {
          item = this.matTree.treeControl.dataNodes?.[i]?.children?.[0]?.children?.[0];
          this.updateSelectedOptions(item, data, i);
        }
      }
    }
  }

  updateSelectedOptions(item: AnyType, data: AssetNestedTreeNode[], i: number) {
    const expiryDate = data[i].expiryDate;
    if (item) {
      if (this.dateNotExpiredPipe.transform(expiryDate) || this.canPipe.transform(this.AnyAsset, 'expired.download')) {
        this.matTree.itemToggle(true, item);
        if (item.parent?.parent) {
          this.matTree.treeControl.expandDescendants(item.parent?.parent);
        }
      } else {
        this.matTree.itemToggle(false, item);
        if (item.parent?.parent) {
          this.matTree.treeControl.collapse(item.parent?.parent);
        }
      }
    }
  }

  async openTerms(event: AnyType) {
    event.preventDefault();
    event.stopPropagation();

    if (this.env.termsConditionsPopup?.onAssetDownload) {
      const dialogRef = this.matDialog.open(TermsConditionsDialogComponent, {
        minHeight: '48rem',
        maxWidth: '40rem',
        data: {
          featureKey: 'assetDownload',
        },
      });
      const result = await firstValueFrom(dialogRef.afterClosed());
      if (!result.accepted) {
        this.downloadForm.get('expiryAndTerms')?.setValue(false);
        return;
      } else {
        this.checked = true;
        this.downloadForm.get('expiryAndTerms')?.setValue(true);
      }
    } else {
      this.checked = true;
      this.downloadForm.get('expiryAndTerms')?.setValue(true);
    }
  }

  private generateDownloadKeys() {
    const keys: string[] = [];
    const selectedItems =
      this.downloadData
        ?.map((item: AnyType) => {
          const isAssetExpired = this.isAssetExpired(item);
          if (item.children.length > 0) {
            const selectedVariants = item.children.map((aspectRatioItem: AnyType) => {
              return !isAssetExpired && aspectRatioItem.children && aspectRatioItem.children.filter((child: AnyType) => child.selected === true);
            });
            return selectedVariants.flat();
          }
        })
        .flat() ?? [];
    selectedItems.forEach((item: AnyType) => {
      if (item) {
        const key = this.createAssetS3Key(item);
        keys.push(key);
      }
    });
    return keys;
  }

  private isAssetExpired(item: AnyType): boolean {
    const licenseRegions = item.licenseRegions || [];
    const selectedExpiredAsset = licenseRegions.filter((region: AnyType) => this.selectedLicenseRegionValues.includes(region.licenseRegion));
    return selectedExpiredAsset.length !== 0 && selectedExpiredAsset.every((expired: AnyType) => !this.dateNotExpiredPipe.transform(expired.expiryDate));
  }

  private createAssetS3Key(item: AnyType): string {
    return createAssetS3Key(item.parent.parent.assetId, item.parent.parent.version, item.parent.aspectRatio, `${item.fileNameRef}.${item.ext}`);
  }

  private timestampForFilename() {
    const date = new Date();
    const addLeadingZero = (toEdit: string): string => {
      if (toEdit.length === 1) {
        return 0 + toEdit;
      }
      return toEdit;
    };
    return `${date.getFullYear().toString()}${addLeadingZero((date.getMonth() + 1).toString())}${addLeadingZero(date.getDate().toString())}_${addLeadingZero(
      addLeadingZero(date.getHours().toString()),
    )}${addLeadingZero(date.getMinutes().toString())}`;
  }

  checkboxClick(checked: boolean) {
    this.checked = checked;
    this.downloadForm.get('expiryAndTerms')?.setValue(checked);
  }

  checkboxEUAIActClick(checked: boolean) {
    this.downloadForm.get('euAIAct')?.setValue(checked);
  }

  onValueChange(event: AnyType) {
    this.selectedLicenseRegionValues = event.value.length ? event.value.map((val: AnyType) => val._id) : [];
    this.updateLicenseRegionFormControl(this.selectedLicenseRegionValues);
  }

  /**
   * Function to update license region form control and set error message
   * @param licenseRegionsValue
   * @returns
   */
  updateLicenseRegionFormControl(licenseRegionsValue: string[]) {
    if (!licenseRegionsValue.length) {
      this.clearErrorMessage();
      return;
    }

    const allLicenseRegions: BuyoutRights[] = this.extractLicenseRegions(licenseRegionsValue);

    this.updateFormErrorState(allLicenseRegions);

    this.setErrorMessage(allLicenseRegions);
  }

  /**
   * Extract license regions based on selected values
   * @param licenseRegionsValue
   * @returns
   */
  private extractLicenseRegions(licenseRegionsValue: string[]): BuyoutRights[] {
    return (
      this.downloadData
        ?.flatMap((data: AnyType) => data.licenseRegions)
        .filter(Boolean)
        .filter((data: AnyType) => licenseRegionsValue.includes(data.licenseRegion)) ?? []
    );
  }

  /**
   * Update form error state based on license regions
   * @param allLicenseRegions
   */
  private updateFormErrorState(allLicenseRegions: BuyoutRights[]) {
    const everyExpiredLicenseRegion =
      allLicenseRegions.length !== 0 && allLicenseRegions.every((region) => !this.dateNotExpiredPipe.transform(region.expiryDate));
    setTimeout(() => {
      const control = this.downloadForm.get('licenseRegion');
      if (control) {
        if (everyExpiredLicenseRegion) {
          control.setErrors({ expired: true });
        } else {
          control.setErrors(null);
        }
      }
    }, 0);
  }

  /**
   * Set error message based on license regions
   * @param allLicenseRegions
   */
  private setErrorMessage(allLicenseRegions: BuyoutRights[]) {
    const someExpiredLicenseRegion = allLicenseRegions.some((region) => !this.dateNotExpiredPipe.transform(region.expiryDate));
    this.errorMsg = someExpiredLicenseRegion
      ? $localize`Some assets have expired in the selected license regions.` +
        ' ' +
        $localize`For more details, please check the individual assets and their buyout rights. Assets expired in all regions will not be downloaded.`
      : '';
  }

  /**
   * Clear error message
   */
  private clearErrorMessage() {
    this.errorMsg = '';
  }
}
